import { GraphQLError } from "graphql"

import { getCustomer } from "@/graphql/schemas/shared/utils/getCustomer"
import { CommerceService } from "@/services/commerce"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { getArticlesBySku } from "../../shared/utils/getArticleInformation"
import { getSkuCalculatedPrice } from "../../shared/utils/getPrices"
import { getStoreKeysWithChannels } from "../../shared/utils/getStoreKeysWithChannels"
import {
  addCustomLineItemAction,
  findCustomLineItemUpdates,
  findTargetCartCustomLineItem,
  formDefaultInsertAction,
  getUpdatedLineItem,
  removeCustomLineItemAction,
} from "../utils/cartHelpers"
import { createCustomerCart } from "../utils/createCart"
import { getActiveCustomerCart } from "../utils/getCart"
import { getItemsWithSelections } from "../utils/getItemsWithSelections"

const commerceApi = CommerceService.api()

export const mutationTransferOrderItemsToCart = {
  async transferOrderItemsToCart(_parent, args, context, _info) {
    let { customerId, customerNumber, lang, cdn, loggedInBranch, orderNumber, withPrice } = args

    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.CARTS,
      operation: Operations.UPDATE,
    })

    if (!customerId || !orderNumber) {
      throw new Error("Missing data")
    }

    const customer = await getCustomer({ id: customerId, number: customerNumber })

    if (!customer) {
      throw new Error("Customer not found")
    }

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: customer.customerNumber,
    })

    const customerCarts = await getActiveCustomerCart(customer.id, user.employeeNumber)
    let customerCart = customerCarts[0]

    if (!customerCart || customerCart.cartState !== "Active") {
      customerCart = await createCustomerCart(customer, null, "EUR", null, null, user)
    }

    if (!customerCart) {
      throw new Error("Customer does not have an active cart and new cart could not be created")
    }

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const { currencyCode } = customerCart.totalPrice

    const { orderItems, isReservationTransfer, reservationNumber, isPicklistTransfer } = await getTransferItems(
      orderNumber,
      withPrice,
      loggedInBranch,
      context,
      customerId,
      customer.customerNumber,
    )

    if (
      customerCart?.custom?.fields?.reservationNumber &&
      customerCart?.custom?.fields?.reservationNumber?.length > 0 &&
      isReservationTransfer
    ) {
      throw new GraphQLError("CART_HAS_RESERVATION", {
        extensions: {
          code: "CART_HAS_RESERVATION",
        },
      })
    }

    if (
      customerCart?.custom?.fields?.picklistNumber &&
      customerCart?.custom?.fields?.picklistNumber?.length > 0 &&
      isPicklistTransfer
    ) {
      throw new GraphQLError("CART_HAS_PICKLIST", {
        extensions: {
          code: "CART_HAS_PICKLIST",
        },
      })
    }

    const { stores: customerStores, customerGroup } = customer

    const storeKeysWithChannels = await getStoreKeysWithChannels(customerStores)

    const totalItems = customerCart?.customLineItems?.length || 0

    const itemsWithSelections = await getItemsWithSelections(orderItems, totalItems)
    if (!itemsWithSelections || itemsWithSelections.length < 1) {
      throw new Error("Nothing to add!")
    }

    const totalProjectedItems = Number(itemsWithSelections.length) + Number(customerCart?.customLineItems?.length || 0)
    if (totalProjectedItems > 200) {
      throw new GraphQLError("TOO_MANY_ITEMS", {
        extensions: {
          code: "TOO_MANY_ITEMS",
        },
      })
    }

    const actions = await getActions(
      itemsWithSelections,
      orderItems,
      storeKeysWithChannels,
      customerGroup,
      currencyCode,
      customerCart,
    )

    if (actions.length === 0) {
      throw new Error("Nothing to add!")
    }

    if (isReservationTransfer && reservationNumber) {
      const reservationAction = getReservationActions(
        customerCart?.custom?.type?.id,
        customerCart.custom.fields,
        reservationNumber,
      )
      if (reservationAction) actions.push(reservationAction)
    }

    if (isPicklistTransfer && reservationNumber) {
      const picklistAction = getPicklistActions(
        customerCart?.custom?.type?.id,
        customerCart.custom.fields,
        reservationNumber,
      )
      if (picklistAction) actions.push(picklistAction)
    }

    const payload = {
      version: customerCart.version,
      actions,
      storeKey: customerCart?.store?.key,
    }

    const updatedCart = (
      await commerceApi
        .carts()
        .withId({ ID: customerCart.id })
        .post({
          body: JSON.stringify(payload),
        })
        .execute()
    )?.body

    return updatedCart
  },
}

/**
 * Retrieves a list of order items with formed article data from order
 *
 * @param {string} orderNumber
 * @param {boolean} withPrice
 * @param {string} loggedInBranch
 * @param {SanityClient} client
 * @param {string} customerId
 *
 * @returns {Promise<{orderItems: Array, isReservationTransfer: boolean, reservationNumber: string|null}>}
 */
const getTransferItems = async (orderNumber, withPrice = false, loggedInBranch, client, customerId, customerNumber) => {
  const { orderItems, isReservationTransfer, reservationNumber, isPicklistTransfer } = await findOrderItems(
    orderNumber,
    customerNumber,
  )

  const skus = orderItems?.map((item) => item.sku)

  const articles = await getArticlesBySku({
    context: client,
    skus,
    branchId: loggedInBranch,
    customerId,
  })

  const enrichedOrderArticles = orderItems?.map((item) => {
    const article = articles.find((article) => article.sku === item.sku)

    if (!article) return null
    const sellableInDecimals = article?.sellableInDecimals

    let articlePrice
    if (withPrice) {
      articlePrice = sellableInDecimals ? item?.price / 1000 : item?.price
    }

    return {
      sku: item?.sku,
      quantity: item?.amount,
      newPrice: articlePrice,
      discountPercent: item?.discount,
      productKey: article?.productRef?.key,
      amountBilling: article?.amountBilling,
      ...(item?.additionalComment && { additionalComment: item?.additionalComment }),
      sellableInDecimals,
      allowMultiple: true, // When copying an order, a new item should always be created in the cart - https://reply.atlassian.net/browse/EINZA-1399?focusedCommentId=52684
    }
  })

  return {
    orderItems: enrichedOrderArticles.filter((article) => article),
    isReservationTransfer,
    reservationNumber,
    isPicklistTransfer,
  }
}

/**
 * Finds order and removes all the items without SKU
 *
 * @param {string} orderNumber
 * @param {string} customerNumber
 *
 * @returns {Promise<{orderItems: Array, isReservationTransfer: boolean, reservationNumber: string|null}>}
 */
const findOrderItems = async (orderNumber, customerNumber) => {
  const graphiQLQueryObject = [
    {
      method: "findOrder",
      variables: { orderNumber },
      fields: ["orderNumber", "customerNumber", "orderType", "orderNumber2", "picklist"],
      expandedFields: {
        items: ["sku", "quantity", "amount", "price", "discount", "additionalComment: description2"],
      },
    },
  ]

  const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)
  const customerOrderResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data?.findOrder

  if (!customerOrderResponse) throw new Error("Customer order by number not found")
  if (!customerOrderResponse.items || customerOrderResponse.items.length === 0) {
    throw new Error("Customer order items not found")
  }

  const isReservationTransfer = customerOrderResponse?.orderType === "RB"
  const isPicklistTransfer = customerOrderResponse?.picklist
  let reservationNumber = null
  if (isReservationTransfer || isPicklistTransfer) {
    // Use customerOrderResponse?.orderNumber (the long order number from API response) for picklistNumber.
    // orderNumber from the API response is the long order number (orderNumberLong), which is needed
    // for lookups in picklistOrderType resolver. The short order number (orderNumber2) is available
    // via picklistOrderNumber2 resolver.
    if (!customerOrderResponse?.orderNumber) {
      throw new Error("Order number not found in API response")
    }

    reservationNumber = getTransferReservationValues(
      customerOrderResponse.orderNumber,
      customerOrderResponse?.customerNumber,
      customerNumber,
    )
  }

  return {
    orderItems: customerOrderResponse?.items?.filter((item) => item.sku && item.quantity),
    isReservationTransfer,
    reservationNumber,
    isPicklistTransfer,
  }
}

/**
 * Forms cart update actions for custom line items
 *
 * @param {array} itemsWithSelections
 * @param {array} orderItems
 * @param {array} storeKeysWithChannels
 * @param {object} customerGroup
 * @param {string} currencyCode
 * @param {object} customerCart
 * @returns {Promise<array>}
 */
const getActions = async (
  itemsWithSelections,
  orderItems,
  storeKeysWithChannels,
  customerGroup,
  currencyCode,
  customerCart,
) => {
  let actions = []

  // custom type created for custom line custom fields
  const customTypeLineItem = (await commerceApi.types().withKey({ key: "customCustomLineItemTypeKey" }).get().execute())
    ?.body

  await Promise.all(
    itemsWithSelections.map(async (item) => {
      const orderItemConfig = orderItems.find((orderItem) => orderItem.sku === item.sku)
      let sellableInDecimals = orderItemConfig?.sellableInDecimals || false
      let amountBilling = orderItemConfig?.amountBilling || 1

      const articleConfig = { sellableInDecimals, amountBilling }

      let itemQuantity = Number(item.quantity)

      if (articleConfig?.sellableInDecimals) {
        itemQuantity = Math.round(itemQuantity * 1000)
      }

      const { customLineItems } = customerCart

      // recalculate the price
      const newPrice = item?.newPrice || typeof item?.newPrice === "number" ? Number(item?.newPrice) * 100 : null

      const price = await getSkuCalculatedPrice({
        sku: item.sku,
        channels: storeKeysWithChannels,
        group: customerGroup,
        country: customerCart.billingAddress.country,
        discountPercent: item?.discountPercent,
        customPrice: newPrice,
        currencyCode,
        articleConfig,
        quantity: itemQuantity,
      })

      // price does not exist in commercetools and no line items are present in the cart
      // will not be able to form custom line item as not enough information is present
      if (customLineItems.length <= 0 && !price) {
        return null
      }

      const defaultInsertAction = formDefaultInsertAction({
        cartId: customerCart.id,
        item,
        price,
        quantity: itemQuantity,
        customTypeId: customTypeLineItem.id,
      })

      // forming a new line item, no line items are present in the cart
      if (customLineItems.length <= 0) {
        actions.push(defaultInsertAction)

        return
      }

      const targetCustomLineItem = findTargetCartCustomLineItem(customLineItems, item)

      // Item was not in the cart, return action that adds it to the cart
      if (!targetCustomLineItem) return actions.push(defaultInsertAction)

      const customFields = targetCustomLineItem.custom.fields

      // recalculate the price
      const lineItemCustomPrice = await getSkuCalculatedPrice({
        sku: item.sku,
        channels: storeKeysWithChannels,
        group: customerGroup,
        country: customerCart.billingAddress.country,
        discountPercent: item?.discountPercent || customFields?.discountPercent,
        customPrice: item?.newPrice || customFields?.customPrice?.centAmount,
        currencyCode,
        articleConfig,
        quantity: Number(itemQuantity) + Number(targetCustomLineItem.quantity),
      })

      const lineItemUpdates = findCustomLineItemUpdates(customFields, item)

      let updatedCustomLineItem = getUpdatedLineItem({
        targetCustomLineItem,
        valuesToUpdate: item,
        customTypeId: customTypeLineItem.id,
        price: lineItemCustomPrice,
        lineItemUpdates,
      })

      if (customFields?.priceId !== lineItemCustomPrice.key || lineItemUpdates) {
        actions.push(removeCustomLineItemAction(targetCustomLineItem?.id))

        actions.push(
          addCustomLineItemAction(
            updatedCustomLineItem,
            currencyCode,
            lineItemCustomPrice,
            customerCart.id,
            Number(itemQuantity) + Number(targetCustomLineItem.quantity),
          ),
        )

        return
      }

      actions.push(
        addCustomLineItemAction(
          updatedCustomLineItem,
          currencyCode,
          lineItemCustomPrice,
          customerCart.id,
          Number(itemQuantity),
        ),
      )

      return
    }),
  )
  return actions
}

/**
 * @param {string} customTypeCartId
 * @param {object} customCartFields
 * @param {string} reservationNumber
 * @returns {object|null}
 */
const getReservationActions = (customTypeCartId, customCartFields, reservationNumber) => {
  if (!customTypeCartId) return null

  /** @type {string} */
  const createdByEmployeeNumber = customCartFields.createdByEmployeeNumber
  const freightSurcharge = customCartFields?.freightSurcharge

  if (!reservationNumber || !createdByEmployeeNumber) return null

  return {
    action: "setCustomType",
    type: {
      typeId: "type",
      id: customTypeCartId,
    },
    fields: {
      ...(freightSurcharge && { freightSurcharge }),
      createdByEmployeeNumber,
      reservationNumber,
    },
  }
}
/**
 * @param {string} customTypeCartId
 * @param {object} customCartFields
 * @param {string} picklistNumber - The order number for picklist (ERP document number)
 * @returns {object|null}
 */
const getPicklistActions = (customTypeCartId, customCartFields, picklistNumber) => {
  if (!customTypeCartId) return null

  /** @type {string} */
  const createdByEmployeeNumber = customCartFields.createdByEmployeeNumber
  const freightSurcharge = customCartFields?.freightSurcharge

  if (!picklistNumber || !createdByEmployeeNumber) return null

  return {
    action: "setCustomType",
    type: {
      typeId: "type",
      id: customTypeCartId,
    },
    fields: {
      ...(freightSurcharge && { freightSurcharge }),
      createdByEmployeeNumber,
      picklistNumber,
    },
  }
}
/**
 * Based on given values checks if items can be transfered to the cart and returns formulated values or throws an error
 *
 * @param {string} orderNumber
 * @param {string} responseCustomerNumber
 * @param {string} cartCustomerNumber
 *
 * @returns {string}
 * @throws {Error}
 */
const getTransferReservationValues = (orderNumber, responseCustomerNumber, cartCustomerNumber) => {
  if (responseCustomerNumber !== cartCustomerNumber) {
    throw new Error("Customer number does not match")
  }

  if (!orderNumber) {
    throw new Error("Missing orderNumber for reservation transfer")
  }

  return `${orderNumber}`
}
